<template>
  <article class="bg-white pb-6 md:pb-12">
    <template v-if="articleQA">
      <div class="article-head-padding bg-white">
        <div class="container">
          <div class="grid-container md:px-11">
            <ArticleTop
              class="col-span-6 sm:col-span-12 mb-5 md:mb-6 pt-4"
              :article="articleQA"
            />
            <ArticleHeadQA :article="articleQA" />
            <h1
              class="headline-m col-span-6 sm:col-span-12 mb-5 md:mb-8"
              v-html="articleQA.qtitle"
            />
          </div>
        </div>
      </div>
      <div ref="container" class="pt-4 container">
        <div class="grid-container pt-3 md:pt-8 md:px-11">
          <div class="col-span-6 sm:col-span-1"></div>
          <div ref="content" class="col-span-6 sm:col-span-8 content">
            <ArticleRobotDisclaimer
              class="mb-8"
              :text="
                articleQA.typeId === 4
                  ? $t('robotDisclaimer.mayor')
                  : $t('robotDisclaimer.minister')
              "
            />
            <ArticleComponentsQa
              :question-title="articleQA.qid ?? ''"
              :question-text="articleQA.qtext ?? ''"
              :answer-title="articleQA.atitle ?? ''"
              :answer-text="articleQA.atext ?? ''"
              :answer-date="
                articleQA.adate ? new Date(articleQA.adate) : new Date()
              "
            />
            <ArticleTags
              v-if="tagList.length > 0"
              :tags="tagList"
              class="my-8 no-print"
            />
          </div>
          <aside class="col-span-6 mt-8 md:mt-0 sm:col-span-3">
            <div class="h-full no-print sidebar">
              <ListNames class="hidden md:block" :count="3" />
              <ListJobs :count="3" :label="'NewestJobs'" />
              <ListMostRead class="hidden md:block" />
              <div
                v-if="config.public.site.adProvider === 'Concept'"
                class="sticky top-16"
              >
                <AdsConceptAd
                  :id="`${config.public.site.ads.placementPrefix}-rec1`"
                  class="hidden lg:block"
                />
                <AdsConceptAd
                  :id="`${config.public.site.ads.placementPrefix}-mob2`"
                  class="lg:hidden block"
                  full-width
                />
              </div>
            </div>
          </aside>
        </div>
      </div>
      <ListJobs isSlider :count="3" :label="'NewestJobs'" />
      <AdsConceptAd
        :id="`${config.public.site.ads.placementPrefix}-lb2`"
        full-width
        class="hidden md:block"
      />
      <AdsConceptAd
        :id="`${config.public.site.ads.placementPrefix}-mob3`"
        class="lg:hidden block"
        full-width
      />
    </template>
  </article>
</template>

<script setup lang="ts">
import type { TagDTO } from '~/typesAuto/apicore/v1'

const route = useRoute()
const nuxtApp = useNuxtApp()
const config = useRuntimeConfig()

const indexStore = useIndexStore()
const userStore = useUserStore()

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
})

const { id } = toRefs(props)
const currentPaperId = indexStore.currentPaper?.RecordId ?? 1

const asyncDataKey = `${id.value}-${route.name?.toString()}-${currentPaperId}${
  userStore.isLoggedIn
}`
const { data: articleQA, error } = await useAsyncData(
  asyncDataKey,
  async () => {
    const routeName = route.name?.toString()
    if (!routeName) {
      return
    }

    return await nuxtApp.$api.content.QAFull(id.value, currentPaperId)
  }
)

if (error.value) {
  throw createError({
    statusCode:
      (error.value as Error & { statusCode: number }).statusCode ?? 404,
    message: error.value.message,
  })
}

const tagList = computed(() => {
  const resultList = [] as TagDTO[]
  if (!articleQA.value || !articleQA.value.relQAHouses) {
    return resultList
  }

  for (const item of articleQA.value.relQAHouses) {
    if (!item.house?.name) {
      continue
    }

    const articleTag = { id: item.house.id, name: item.house.name } as TagDTO
    resultList.push(articleTag)
  }

  return resultList
})

const content = ref<HTMLDivElement>()

const getMetaTags = () => {
  let titletmp = config.public.site.meta.title.postfix.long

  if (!articleQA.value) {
    return {
      title: titletmp,
    }
  }

  if (
    nuxtApp.runWithContext(() => {
      !isMm()
    }) &&
    indexStore.currentPaper &&
    indexStore.currentPaper.RecordId > 1
  ) {
    titletmp =
      config.public.site.meta.title.postfix.short + indexStore.currentPaper.Name
  }

  const metaData = [
    {
      hid: 'twitter:card',
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      hid: 'twitter:site',
      name: 'twitter:site',
      content: `@${config.public.site.twitter}`,
    },
    {
      hid: 'og:site_name',
      name: 'og:site_name',
      content: indexStore.currentPaper?.Name,
    },
  ]

  titletmp = articleQA.value.qtitle
    ? articleQA.value.qtitle + titletmp
    : titletmp

  return {
    title: titletmp,
    meta: metaData,
  }
}
useHead(() => {
  return getMetaTags()
})
</script>

<style lang="scss" scoped>
@import '@/assets/css/articleScoped.css';
</style>
<style lang="scss">
@import '@/assets/css/article.css';
</style>
